/**
 * Date Utilities - Framework-agnostic
 * Pure TypeScript date formatting and manipulation
 */

/**
 * Format date to YYYY-MM-DD
 */
export function formatDateYYYYMMDD(date: Date | string | number): string {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Format date to DD/MM/YYYY
 */
export function formatDateDDMMYYYY(date: Date | string | number): string {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${day}/${month}/${year}`;
}

/**
 * Format date to MM/DD/YYYY
 */
export function formatDateMMDDYYYY(date: Date | string | number): string {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${month}/${day}/${year}`;
}

/**
 * Format date with time to YYYY-MM-DD HH:mm:ss
 */
export function formatDateTime(date: Date | string | number): string {
  const d = new Date(date);
  const datePart = formatDateYYYYMMDD(d);
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const seconds = String(d.getSeconds()).padStart(2, "0");
  return `${datePart} ${hours}:${minutes}:${seconds}`;
}

/**
 * Format date from object { year, month, day }
 */
export function formatDateFromObject(dateObj: {
  year: number;
  month: number;
  day: number;
}): string {
  const date = new Date(dateObj.year, dateObj.month - 1, dateObj.day);
  return formatDateYYYYMMDD(date);
}

/**
 * Parse date string to Date object
 */
export function parseDate(dateString: string): Date | null {
  if (!dateString) return null;

  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Check if date is valid
 */
export function isValidDate(date: any): boolean {
  if (!date) return false;
  const d = new Date(date);
  return !isNaN(d.getTime());
}

/**
 * Check if date is in the past
 */
export function isDateInPast(date: Date | string | number): boolean {
  const d = new Date(date);
  return d.getTime() < Date.now();
}

/**
 * Check if date is in the future
 */
export function isDateInFuture(date: Date | string | number): boolean {
  const d = new Date(date);
  return d.getTime() > Date.now();
}

/**
 * Get difference in days between two dates
 */
export function daysDifference(
  date1: Date | string,
  date2: Date | string,
): number {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  const diff = Math.abs(d1.getTime() - d2.getTime());
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

/**
 * Add days to a date
 */
export function addDays(date: Date | string, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

/**
 * Add months to a date
 */
export function addMonths(date: Date | string, months: number): Date {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
}

/**
 * Get start of day
 */
export function startOfDay(date: Date | string): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Get end of day
 */
export function endOfDay(date: Date | string): Date {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

/**
 * Get start of month
 */
export function startOfMonth(date: Date | string): Date {
  const d = new Date(date);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Get end of month
 */
export function endOfMonth(date: Date | string): Date {
  const d = new Date(date);
  d.setMonth(d.getMonth() + 1, 0);
  d.setHours(23, 59, 59, 999);
  return d;
}

/**
 * Format time ago (e.g., "2 hours ago", "3 days ago")
 */
export function timeAgo(date: Date | string | number): string {
  const d = new Date(date);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - d.getTime()) / 1000);

  const intervals: { [key: string]: number } = {
    year: 31536000,
    month: 2592000,
    week: 604800,
    day: 86400,
    hour: 3600,
    minute: 60,
    second: 1,
  };

  for (const [name, secondsInInterval] of Object.entries(intervals)) {
    const interval = Math.floor(seconds / secondsInInterval);
    if (interval >= 1) {
      return interval === 1 ? `1 ${name} ago` : `${interval} ${name}s ago`;
    }
  }

  return "just now";
}

/**
 * Get current timestamp in seconds
 */
export function currentTimestamp(): number {
  return Math.floor(Date.now() / 1000);
}

/**
 * Convert timestamp to Date
 */
export function timestampToDate(timestamp: number): Date {
  return new Date(timestamp * 1000);
}

/**
 * Get ISO string
 */
export function toISOString(date: Date | string): string {
  return new Date(date).toISOString();
}
